import { defineStore } from 'pinia'
import { ref, watch } from 'vue'

export type Theme = 'light' | 'dark'

// Check if system prefers dark mode
function getSystemPreference(): boolean {
  return window.matchMedia('(prefers-color-scheme: dark)').matches
}

// Get initial theme - migrate old 'system' setting to actual preference
function getInitialTheme(): Theme {
  const stored = localStorage.getItem('theme')
  if (stored === 'light' || stored === 'dark') {
    return stored
  }
  // For 'system' or no preference, use system preference
  return getSystemPreference() ? 'dark' : 'light'
}

export const useThemeStore = defineStore('theme', () => {
  // Initialize from localStorage, migrating 'system' to actual preference
  const theme = ref<Theme>(getInitialTheme())

  // Track the actual applied theme
  const isDark = ref(false)

  // Apply the theme to the document
  function applyTheme() {
    isDark.value = theme.value === 'dark'

    if (isDark.value) {
      document.documentElement.classList.add('dark')
    } else {
      document.documentElement.classList.remove('dark')
    }
  }

  // Set theme and persist to localStorage
  function setTheme(newTheme: Theme) {
    theme.value = newTheme
    localStorage.setItem('theme', newTheme)
    applyTheme()
  }

  // Toggle between light and dark
  function toggleTheme() {
    setTheme(isDark.value ? 'light' : 'dark')
  }

  // Initialize on store creation
  function init() {
    // Save the resolved theme to localStorage if it was 'system' before
    const stored = localStorage.getItem('theme')
    if (stored !== 'light' && stored !== 'dark') {
      localStorage.setItem('theme', theme.value)
    }
    applyTheme()
  }

  // Watch for theme changes
  watch(theme, applyTheme)

  return {
    theme,
    isDark,
    setTheme,
    toggleTheme,
    init
  }
})
